import React, { useState, useEffect, useCallback } from 'react';
import { Descriptions, Table, Tag, Button, Card, Tabs, Alert, Timeline, Spin } from 'antd';
import { SupplierOrder } from '../types.ts';
import { supplierAPI } from '../services/api.ts';
import { formatCurrency } from '../utils/numberFormatting.ts';

const { TabPane } = Tabs;

// Etichette e colori per gli eventi della cronologia (audit-log)
const EVENT_LABELS: { [key: string]: string } = {
  CREATE: 'Ordine creato',
  UPDATE: 'Modifica',
  STATUS_CHANGE: 'Cambio stato',
  DELETE: 'Eliminazione',
  PRODUCT_ADDED: 'Prodotto aggiunto',
  PRODUCT_REMOVED: 'Prodotto rimosso',
  PRODUCT_MODIFIED: 'Prodotto modificato',
  RESERVATION_ADDED: 'Prenotazione aggiunta',
  RESERVATION_REMOVED: 'Prenotazione rimossa',
  RESERVATION_MODIFIED: 'Prenotazione modificata',
  RECEIVE: 'Merce ricevuta',
  CONVERT: 'Conversione',
};

const getEventLabel = (eventType: string) => EVENT_LABELS[eventType] || eventType;

const getEventColor = (eventType: string) => {
  if (['CREATE', 'PRODUCT_ADDED', 'RESERVATION_ADDED', 'RECEIVE'].includes(eventType)) return 'green';
  if (['DELETE', 'PRODUCT_REMOVED', 'RESERVATION_REMOVED'].includes(eventType)) return 'red';
  if (['STATUS_CHANGE', 'CONVERT'].includes(eventType)) return 'blue';
  return 'gray';
};

interface SupplierOrderDetailsModalProps {
  order: SupplierOrder | null;
  onClose: () => void;
}

const SupplierOrderDetailsModal: React.FC<SupplierOrderDetailsModalProps> = ({
  order,
  onClose
}) => {
  const [loading, setLoading] = useState(false);
  const [orderDetails, setOrderDetails] = useState<any[]>([]);
  const [orderHistory, setOrderHistory] = useState<any[]>([]);
  // Ordine completo dal GET /{id}: la riga dell'elenco porta del fornitore solo id e nome,
  // qui servono anche contatti/indirizzo per la scheda "Informazioni Fornitore"
  const [fullOrder, setFullOrder] = useState<SupplierOrder | null>(null);

  const loadOrderDetails = useCallback(async () => {
    if (!order) return;
    
    setLoading(true);
    try {
      const data = await supplierAPI.getSupplierOrder(order.id);
      setFullOrder(data);
      setOrderDetails(data.order_details || []);
    } catch (error) {
      console.error('Error loading order details:', error);
    } finally {
      setLoading(false);
    }
  }, [order]);

  const loadOrderHistory = useCallback(async () => {
    if (!order) return;

    try {
      const history = await supplierAPI.getSupplierOrderHistory(order.id);
      setOrderHistory(history || []);
    } catch (error) {
      console.error('Error loading order history:', error);
      setOrderHistory([]);
    }
  }, [order]);

  useEffect(() => {
    if (order) {
      loadOrderDetails();
      loadOrderHistory();
    }
  }, [order, loadOrderDetails, loadOrderHistory]);

  if (!order) return null;

  const supplierInfo = fullOrder?.supplier ?? order.supplier;

  const getStatusColor = (statusName: string) => {
    const colors: { [key: string]: string } = {
      'Nuovo': 'blue',
      'In Attesa': 'gold',
      'In Lavorazione': 'orange',
      'Confermato': 'green',
      'In Produzione': 'cyan',
      'Pronto per Spedizione': 'geekblue',
      'Spedito': 'purple',
      'In Transito': 'orange',
      'Consegnato': 'lime',
      'Annullato': 'red'
    };
    return colors[statusName] || 'default';
  };

  // Genera identificatore composto per prodotto
  const generateCompositeIdentifier = (internal_code: string, size?: string, color?: string) => {
    if (!size && !color) {
      return internal_code;
    } else if (size && !color) {
      return `${internal_code}-${size}`;
    } else if (!size && color) {
      return `${internal_code}--${color}`;
    } else {
      return `${internal_code}-${size}-${color}`;
    }
  };

  const detailsColumns = [
    {
      title: 'Prodotto',
      dataIndex: ['product', 'internal_product_code'],
      key: 'product_code',
      render: (code: string, record: any) => {
        const product = record.product;
        if (!product) return 'N/A';
        
        const compositeIdentifier = generateCompositeIdentifier(
          product.internal_product_code,
          product.size,
          product.color
        );
        
        return (
          <div>
            <strong>{compositeIdentifier}</strong>
            {product.client_product_code && (
              <div style={{ fontSize: '13px', color: '#333', marginTop: '2px' }}>
                {product.client_product_code}
              </div>
            )}
            <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
              {product.product_description}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Ordine Cliente',
      key: 'customer_order',
      render: (record: any) => record.customer_order?.order_number
        || (record.customer_order_id ? `#${record.customer_order_id}` : '-'),
    },
    {
      title: 'Quantità',
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'center' as const,
      render: (quantity: number, record: any) => (
        <div>
          {quantity}
          {(record.quantity_cancelled || 0) > 0 && (
            <div style={{ fontSize: '12px', color: '#cf1322' }}>
              Annullati: {record.quantity_cancelled}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Costo Unitario',
      dataIndex: 'cost',
      key: 'cost',
      render: (cost: number) => `€ ${cost ? formatCurrency(cost) : '0,0000'}`,
    },
    {
      title: 'Totale',
      key: 'total',
      render: (record: any) => {
        const total = (record.quantity || 0) * (record.cost || 0);
        return `€ ${formatCurrency(total)}`;
      },
    },
    {
      title: 'Note',
      dataIndex: 'notes',
      key: 'notes',
      render: (notes: string) => notes || '-',
    },
  ];

  const calculateTotal = () => {
    return orderDetails.reduce((sum, detail) => {
      return sum + ((detail.quantity || 0) * (detail.cost || 0));
    }, 0);
  };

  return (
    <Spin spinning={loading}>
      <Tabs defaultActiveKey="1">
        <TabPane tab="Informazioni Generali" key="1">
          <Descriptions bordered column={2}>
            <Descriptions.Item label="Numero Ordine" span={2}>
              <strong>{order.order_number}</strong>
            </Descriptions.Item>
            <Descriptions.Item label="Fornitore">
              {supplierInfo?.name || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Email Fornitore">
              {supplierInfo?.email || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Data Ordine">
              {new Date(order.order_date).toLocaleDateString()}
            </Descriptions.Item>
            <Descriptions.Item label="Data Consegna Richiesta">
              {order.delivery_date ? new Date(order.delivery_date).toLocaleDateString() : 'Non specificata'}
            </Descriptions.Item>
            <Descriptions.Item label="Stato">
              <Tag color={getStatusColor(order.order_status?.status_name || '')}>
                {order.order_status?.status_name || 'N/A'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Importo Totale" span={2}>
              <strong style={{ fontSize: '16px', color: '#1890ff' }}>
                € {order.total_amount ? formatCurrency(order.total_amount) : formatCurrency(calculateTotal())}
              </strong>
            </Descriptions.Item>
            {order.notes && (
              <Descriptions.Item label="Note" span={2}>
                {order.notes}
              </Descriptions.Item>
            )}
          </Descriptions>
        </TabPane>

        <TabPane tab="Dettagli Prodotti" key="2">
          <Table
            dataSource={orderDetails}
            columns={detailsColumns}
            pagination={false}
            rowKey="id"
            size="small"
          />
          
          <div style={{ marginTop: 16, textAlign: 'right' }}>
            <strong>Totale Ordine: € {order.total_amount ? formatCurrency(order.total_amount) : formatCurrency(calculateTotal())}</strong>
          </div>
        </TabPane>

        <TabPane tab="Informazioni Fornitore" key="3">
          {supplierInfo ? (
            <Card size="small">
              <Descriptions bordered column={1}>
                <Descriptions.Item label="Nome Fornitore">
                  {supplierInfo.name}
                </Descriptions.Item>
                {supplierInfo.contact_person && (
                  <Descriptions.Item label="Persona di Contatto">
                    {supplierInfo.contact_person}
                  </Descriptions.Item>
                )}
                {supplierInfo.email && (
                  <Descriptions.Item label="Email">
                    {supplierInfo.email}
                  </Descriptions.Item>
                )}
                {supplierInfo.phone && (
                  <Descriptions.Item label="Telefono">
                    {supplierInfo.phone}
                  </Descriptions.Item>
                )}
                {supplierInfo.address && (
                  <Descriptions.Item label="Indirizzo">
                    {supplierInfo.address}
                    {supplierInfo.city && `, ${supplierInfo.city}`}
                    {supplierInfo.postal_code && ` ${supplierInfo.postal_code}`}
                    {supplierInfo.country && `, ${supplierInfo.country}`}
                  </Descriptions.Item>
                )}
                {supplierInfo.website && (
                  <Descriptions.Item label="Sito Web">
                    <a href={supplierInfo.website} target="_blank" rel="noopener noreferrer">
                      {supplierInfo.website}
                    </a>
                  </Descriptions.Item>
                )}
                {supplierInfo.notes && (
                  <Descriptions.Item label="Note Fornitore">
                    {supplierInfo.notes}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>
          ) : (
            <Alert message="Informazioni fornitore non disponibili" type="info" />
          )}
        </TabPane>

        <TabPane tab="Cronologia" key="4">
          {orderHistory.length === 0 ? (
            <Alert message="Nessuna operazione registrata per questo ordine" type="info" />
          ) : (
            <Timeline>
              {orderHistory.map((event, index) => (
                <Timeline.Item key={event.id ?? index} color={getEventColor(event.event_type)}>
                  <div>
                    <strong>{getEventLabel(event.event_type)}</strong>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      {new Date(event.created_at).toLocaleString()}
                    </div>
                    {event.description && (
                      <div style={{ marginTop: 4 }}>{event.description}</div>
                    )}
                    {event.user && (
                      <div style={{ fontSize: '12px', color: '#999' }}>
                        da {event.user.username}
                      </div>
                    )}
                  </div>
                </Timeline.Item>
              ))}
            </Timeline>
          )}
        </TabPane>
      </Tabs>

      <div style={{ marginTop: 16, textAlign: 'right' }}>
        <Button onClick={onClose}>Chiudi</Button>
      </div>
    </Spin>
  );
};

export default SupplierOrderDetailsModal;
